import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WaitlistModal from '../components/WaitlistModal';
import PhoneMockup from '../components/PhoneMockup';
import { useTextUnderline } from '../hooks/useTextUnderline';
import { useAnimateOnScroll } from '../hooks/useAnimateOnScroll';

// ─── Hero rotator ───────────────────────────────────────────────────────────
const ROTATOR_ITEMS = ['The Future of Money', 'Seamless Experience', 'Instant Payments'];

function HeroRotator() {
  const items = ROTATOR_ITEMS;
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setIsTransitioning(true);
      setIndex(prev => prev + 1);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (index !== items.length) return;
    resetTimerRef.current = setTimeout(() => {
      setIsTransitioning(false);
      setIndex(0);
    }, 700);
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [index, items.length]);

  const handleTransitionEnd = (e) => {
    if (e.propertyName !== 'transform') return;
    if (index === items.length) {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
      setIsTransitioning(false);
      setIndex(0);
    }
  };

  const extendedItems = [...items, items[0]];

  return (
    <span className="rotator" aria-label="rotating hero text">
      <span
        className="rotator-track"
        onTransitionEnd={handleTransitionEnd}
        style={{
          transition: isTransitioning ? 'transform 650ms cubic-bezier(.2,.9,.2,1)' : 'none',
          transform: `translateY(calc(-1 * var(--rotator-line) * ${index}))`,
        }}
      >
        {extendedItems.map((item, i) => (
          <span key={`${item}-${i}`} className="rotator-item" aria-hidden={i === index ? undefined : 'true'}>
            {item}
          </span>
        ))}
      </span>
    </span>
  );
}

// ─── Tab Section ────────────────────────────────────────────────────────────
function TabsSection() {
  const [activeTab, setActiveTab] = useState('chattak');

  return (
    <section className="section-tabs">
      <div className="container-small">
        <div className="tabs-title">
          <h2>Payment Way With</h2>
        </div>

        <div className="tabs-nav tab" role="tablist" aria-label="Payment comparison">
          <button
            className={`tab-button${activeTab === 'chattak' ? ' active' : ''}`}
            data-tab="chattak" role="tab"
            aria-selected={activeTab === 'chattak'}
            onClick={() => setActiveTab('chattak')}
          >
            <div className="tab-img"><img src="/images/logo.png" alt="Chattak Logo" /></div>
            <span>Chattak</span>
          </button>
          <button
            className={`tab-button${activeTab === 'regular' ? ' active' : ''}`}
            data-tab="regular" role="tab"
            aria-selected={activeTab === 'regular'}
            onClick={() => setActiveTab('regular')}
          >
            <span>Regular</span>
          </button>
        </div>

        <div className="tabs-wrapper">
          <div className="tabs-content">
            <div className={`tab-panel${activeTab === 'chattak' ? ' active' : ''}`} id="chattak" role="tabpanel">
              <div className="tab-flow">
                <div className="flow-box">
                  <div className="flow-icon"><img src="/icons/user_icon.svg" alt="User Icon" /></div>
                  <h4>User</h4>
                </div>
                <div className="flow-arrow"><img src="/icons/right_arrow_2.svg" alt="Arrow Icon" /></div>
                <div className="flow-box">
                  <div className="flow-icon"><img src="/images/logo.png" alt="Chattak Logo" /></div>
                  <h4>Chattak</h4>
                </div>
                <div className="flow-arrow"><img src="/icons/right_arrow_2.svg" alt="Arrow Icon" /></div>
                <div className="flow-box">
                  <div className="flow-icon"><img src="/icons/merchant_icon.svg" alt="Merchant Icon" /></div>
                  <h4>Merchant</h4>
                </div>
              </div>
            </div>

            <div className={`tab-panel${activeTab === 'regular' ? ' active' : ''}`} id="regular" role="tabpanel">
              <div className="tab-flow vertical">
                <div className="flow-row">
                  {[
                    { icon: '/icons/user_icon.svg', label: 'User' },
                    { icon: '/icons/bank.svg', label: 'Bank/ATM' },
                    { icon: '/icons/cash.svg', label: 'Cash' },
                    { icon: '/icons/merchant_icon.svg', label: 'Merchant' },
                    { icon: '/icons/bank.svg', label: 'Bank/ATM' },
                  ].map((item, i, arr) => (
                    <React.Fragment key={i}>
                      <div className="flow-box small">
                        <div className="flow-icon"><img src={item.icon} alt={item.label} /></div>
                        <p>{item.label}</p>
                      </div>
                      {i < arr.length - 1 && <div className="flow-arrow-small"><img src="/icons/right_arrow_2.svg" alt="" /></div>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── API Dashboard ───────────────────────────────────────────────────────────
function APIDashboard() {
  const [activeTab, setActiveTab] = useState('api');

  const codeBlocks = {
    api: [
      "const payment = await chattak.payments.create({",
      "  amount: 500000,",
      '  currency: "AFN",',
      '  merchant: "Ari***_airlines",',
      '  reference: "order_98423",',
      '  method: "wallet",',
      "});",
    ],
    webhooks: [
      'app.post("/webhooks/chattak", (req, res) => {',
      "  const event = req.body;",
      '  if (event.type === "payment.succeeded") {',
      "    fulfillOrder(event.data);",
      "  }",
      "});",
    ],
    keys: [
      "CHATTAK_SECRET_KEY=sk_live_xxxx",
      "CHATTAK_PUBLIC_KEY=pk_live_xxxx",
    ],
  };

  return (
    <div className="dashboard-mockup api">
      <div className="dashboard-header">
        <div className="dash-tabs" role="tablist" aria-label="API code examples">
          {['api', 'webhooks', 'keys'].map(tab => {
            const label = tab.charAt(0).toUpperCase() + tab.slice(1);
            return (
              <button
                key={tab}
                type="button"
                className={`dash-tab${activeTab === tab ? ' active' : ''}`}
                role="tab"
                aria-selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="code-container">
        <div className="code-block active">
          {codeBlocks[activeTab].map((line, i) => (
            <div key={i} className={`code-line${line.startsWith('  ') ? ' indent' : ''}`}>{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Wizard Section ───────────────────────────────────────────────────────────
function WizardSection() {
  const [activeScreen, setActiveScreen] = useState(1);
  const stepsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const stepIdx = parseInt(entry.target.getAttribute('data-step'));
        if (entry.isIntersecting) {
          setActiveScreen(stepIdx);
        } else if (entry.boundingClientRect.top > 0) {
          setActiveScreen(prev => Math.max(1, stepIdx - 1));
        }
      });
    }, { rootMargin: '0% 0% -50% 0%', threshold: 0.1 });

    stepsRef.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const screens = [
    {
      content: (
        <>
          <div className="phone-header"><div className="back-circle">←</div>Pay Merchant</div>
          <div className="section-label">Choose Payment Source</div>
          <div className="bank-option selected">
            <div className="bank-icon"><img src="/images/logo.png" alt="Chattak" /></div>
            <div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}>Chattak Wallet</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 200, color: '#64748b', marginTop: 2 }}>+93 78 *** ****</div>
            </div>
          </div>
          <button className="btn-primary">Continue</button>
        </>
      )
    },
    {
      content: (
        <>
          <div className="phone-header"><div className="back-circle">←</div>Enter Amount</div>
          <div className="section-label">Payment Amount</div>
          <div className="amount-display">؋ 5,000</div>
          <div className="numpad">
            {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
              <div key={i} className={k !== '' ? 'num-key' : ''}>{k}</div>
            ))}
          </div>
          <button className="btn-primary" style={{ marginTop: 16 }}>Confirm Payment</button>
        </>
      )
    },
    {
      content: (
        <>
          <div className="success-checkmark"><img src="/icons/tick-mark.svg" width="50%" alt="success" /></div>
          <h2 style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '1.6rem', color: '#0f172a', fontWeight: 600 }}>Payment Successful!</h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', fontWeight: 450 }}>Transaction ID: #CH-99281</p>
          <div className="receipt-card">
            <div className="receipt-row"><span className="label">Recipient</span><strong className="value">Ari*** Airline</strong></div>
            <div className="receipt-row"><span className="label">Payment Method</span><strong className="value">Chattak Wallet</strong></div>
            <div className="receipt-row"><span className="label">Total Amount</span><strong className="value amount">؋ 5,000</strong></div>
          </div>
          <button className="btn-secondary">Return to Home</button>
        </>
      )
    }
  ];

  return (
    <section className="section-wizard" id="product">
      <div className="container-large">
        <div className="wizard-parent">
          <div className="wizard-child left">
            <div className="phone-frame">
              <div className="dev-badge">PROTOTYPE</div>
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="status-bar">
                  <span className="phone-time"></span>
                  <span>●●●●</span>
                </div>
                {screens.map((s, i) => (
                  <div key={i} className={`screen-content${activeScreen === i + 1 ? ' active' : ''}`}>
                    {s.content}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="wizard-child right">
            <h2>From Cash to <br /><span className="highlight js-text-underline" data-underline="sm">Digitization</span></h2>
            <p className="description">
              Chattak will revolutionize the way people transact. Whether you're a small business owner or a large enterprise,
              our platform is designed to meet your needs and help you grow your business in the digital age.
            </p>

            <div className="stack-container">
              {[
                { step: 1, title: 'Link & Select', desc: 'Connect your existing bank accounts or mobile wallets. Choose which one you want to use for this specific payment instantly from our secure vault.' },
                { step: 2, title: 'One-Click Auth', desc: "No more long account numbers or IBANs. Just enter the amount or scan a QR code. Your security is handled by Chattak's encrypted bridge." },
                { step: 3, title: 'Instant Settlement', desc: 'Funds move instantly across the network. The merchant receives confirmation immediately, and you get a digital receipt for your records.' },
              ].map(({ step, title, desc }) => (
                <div
                  key={step}
                  className={`step-item${activeScreen > step ? ' stacked' : ''}`}
                  data-step={step}
                  style={{ zIndex: step }}
                  ref={el => stepsRef.current[step - 1] = el}
                >
                  <span className="step-number">Step 0{step}</span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Nebula CTA ───────────────────────────────────────────────────────────────
function NebulaCTA({ onOpenWaitlist }) {
  const containerRef = useRef(null);
  const systemRef = useRef(null);
  const followerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const system = systemRef.current;
    const follower = followerRef.current;
    if (!container || !system || !follower) return;

    let mouseX = 0, mouseY = 0, currentX = 0, currentY = 0;
    const factor = 0.05;
    let rafId;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    const onEnter = () => { system.style.opacity = '1'; };
    const onLeave = () => { system.style.opacity = '0'; };

    function animate() {
      currentX += (mouseX - currentX) * factor;
      currentY += (mouseY - currentY) * factor;
      follower.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      rafId = requestAnimationFrame(animate);
    }

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseenter', onEnter);
    container.addEventListener('mouseleave', onLeave);
    animate();

    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="section-cta" id="cta">
      <div className="container-large">
        <div className="cta-parent" ref={containerRef}>
          <div className="nebula-system" ref={systemRef} style={{ opacity: 0 }}>
            <div className="nebula-follower" ref={followerRef}>
              <div className="nebula-glow-layer layer-1"></div>
              <div className="nebula-glow-layer layer-2"></div>
              <div className="nebula-glow-layer layer-3"></div>
            </div>
          </div>
          <div className="cta-content">
            <h2>Join <span className="highlight-yellow js-text-underline">Chattak</span> today</h2>
            <p>Get in touch to learn how wallet can increase efficiency and speed for your transactions, whether you're a business or an individual.</p>
            <a className="m-button__wrap" onClick={(e) => { e.preventDefault(); onOpenWaitlist(); }} data-btn="up" href="#!">
              <span className="m-b-bg-white" aria-hidden="true"></span>
              <span className="m-b-text" aria-hidden="true">
                <span className="m-b-text-static">Get Early Access</span>
                <span className="m-b-text-up">Get Early Access</span>
              </span>
              <span className="m-b-arrow" aria-hidden="true">
                <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
              </span>
              <span className="sr-only">Get Early Access</span>
            </a>
          </div>
          <div className="cta-circles" aria-hidden="true">
            <div className="cta-circle circle-1"></div>
            <div className="cta-circle circle-2"></div>
            <div className="cta-circle circle-3"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [showTransaction, setShowTransaction] = useState(false);
  useTextUnderline();
  useAnimateOnScroll();

  // Button hover effects
  useEffect(() => {
    const handler = (e) => {
      if (!e.target || typeof e.target.closest !== 'function') return; // ← add this line
      const btn = e.target.closest('[data-btn="up"]');
      if (!btn) return;
      if (e.type === 'mouseenter') btn.classList.add('is-hover');
      if (e.type === 'mouseleave') btn.classList.remove('is-hover');
    };
    document.addEventListener('mouseenter', handler, true);
    document.addEventListener('mouseleave', handler, true);
    return () => {
      document.removeEventListener('mouseenter', handler, true);
      document.removeEventListener('mouseleave', handler, true);
    };
  }, []);

  return (
    <>
      <Navbar onOpenWaitlist={() => setWaitlistOpen(true)} />

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <HeroRotator />
              <br />with <span className="highlight js-text-underline" data-underline="sm">Chattak</span>
            </h1>
            <p>Chattak is revolutionizing how Afghans send, receive, and manage money with our secure electronic payment platform.</p>
            <div className="hero-actions">
              <a href="#cta" className="m-button__wrap" data-btn="up">
                <span className="m-b-bg" aria-hidden="true"></span>
                <span className="m-b-text" aria-hidden="true">
                  <span className="m-b-text-static">Get started</span>
                  <span className="m-b-text-up">Get started</span>
                </span>
                <span className="m-b-arrow" aria-hidden="true">
                  <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                  <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                </span>
                <span className="sr-only">Get started</span>
              </a>
              <a
                href="#!"
                className="m-button__wrap m-button--ghost"
                data-btn="up"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollBy({ top: Math.round(window.innerHeight * 0.9), left: 0, behavior: 'smooth' });
                }}
              >
                <span className="m-b-bg" aria-hidden="true"></span>
                <span className="m-b-text" aria-hidden="true">
                  <span className="m-b-text-static">Learn more</span>
                  <span className="m-b-text-up">Learn more</span>
                </span>
                <span className="m-b-arrow" aria-hidden="true">
                  <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                  <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                </span>
                <span className="sr-only">Learn more</span>
              </a>
            </div>
          </div>

          {/* 3D Payment Flow */}
          <div className="hero-visual">
            <div className="scene" id="scene">
              <svg className="connections-svg" viewBox="0 0 860 640" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                <path id="p1" className="conn-path conn-1" d="M 500 100 L 630 100"/>
                <path id="p2" className="conn-path conn-2" d="M  760 210 Q 793 240 700 240 L 320 240"/>
                <path id="p3" className="conn-path conn-3" d="M 320 278 C 570 279 550 267 550 373"/>
                <path id="p4" className="conn-path conn-4" d="M 700 340 C 780 340 780 340 780 360"/>
                <circle className="conn-dot dot-1"><animateMotion dur="2.4s" repeatCount="indefinite" rotate="auto"><mpath href="#p1"/></animateMotion></circle>
                <circle className="conn-dot dot-2"><animateMotion dur="2.8s" repeatCount="indefinite" rotate="auto" begin="0.4s"><mpath href="#p2"/></animateMotion></circle>
                <circle className="conn-dot dot-3"><animateMotion dur="2.2s" repeatCount="indefinite" rotate="auto" begin="0.8s"><mpath href="#p3"/></animateMotion></circle>
                <circle className="conn-dot dot-4"><animateMotion dur="2s" repeatCount="indefinite" rotate="auto" begin="1.2s"><mpath href="#p4"/></animateMotion></circle>
              </svg>

              <div className="fc card-1 float-0">
                <div className="step-badge">Step 1</div>
                <div className="customer-row">
                  <div className="avatar"><img src="/icons/avatar.png" alt="Customer Avatar" onError={e => { e.target.style.display='none'; e.target.parentElement.innerText='👤'; }} /></div>
                  <div><div className="customer-name">Ahmad K.</div><div className="customer-label">Customer</div></div>
                </div>
                <div className="balance-box">
                  <div className="balance-label">Wallet Balance</div>
                  <div className="balance-val">5,000 &#x60B;</div>
                </div>
              </div>

              <div className="fc card-2 float-1">
                <div className="step-badge">Step 2</div>
                <div className="card-2-top">
                  <div className="merchant-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M3 9l1-5h16l1 5"/><path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0"/>
                      <path d="M5 9v11h14V9"/><rect x="9" y="14" width="6" height="6" rx="1"/>
                    </svg>
                  </div>
                  <span className="pending-badge">Pending</span>
                </div>
                <div className="req-title">Payment Request</div>
                <div className="req-amount">5,000 &#x60B;</div>
                <div className="req-to">To: Merchant Store</div>
                <button className="pay-btn">Pay Now</button>
              </div>

              <div className="fc card-3 float-2">
                <div className="step-badge">Step 3</div>
                <div className="source-title">Select Source</div>
                <div className="source-sub">Select funding source</div>
                <div className="bank-grid">
                  {[
                    { src: '/images/logo.png', alt: 'Chattak' },
                    { src: '/images/AIB_logo.png', alt: 'AIB' },
                    { src: '/images/aub_logo.png', alt: 'AUB' },
                    { src: '/images/iba_logo.png', alt: 'Islamic Bank' },
                  ].map(({ src, alt }) => (
                    <div key={alt} className="bank-item">
                      <img src={src} alt={alt} onError={e => { e.target.style.display='none'; e.target.parentElement.innerHTML=`<span class="bank-fallback">${alt}</span>`; }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="fc card-4 float-3">
                <div className="step-badge">Step 4</div>
                <div className="auth-title">Authorize Payment</div>
                <div className="auth-sub">Secure Transaction</div>
                <div className="auth-table">
                  <div className="auth-row"><span className="auth-lbl">Amount</span><span className="auth-val">5,000 &#x60B;</span></div>
                  <div className="auth-row"><span className="auth-lbl">Fee</span><span className="auth-val">0.00 &#x60B;</span></div>
                  <div className="auth-row total"><span className="auth-lbl">Total</span><span className="auth-val">5,000 &#x60B;</span></div>
                </div>
                <button className="confirm-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Confirm Payment
                </button>
              </div>

              <div className="fc card-5 float-4">
                <div className="step-badge teal">Step 5</div>
                <div className="success-circle">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div className="success-title">Success!</div>
                <div className="success-sub">Transaction<br/>Complete</div>
                <div className="tx-box">
                  <div className="tx-label">Transaction ID</div>
                  <div className="tx-id">#9823711</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <section className="marquee-section">
        <div className="marquee-text">Connecting Financial institutes across the country...</div>
        <div className="marquee-viewport">
          <div className="marquee">
            {['hesabpay_logo.png','AIB_logo.png','AUB_logo.png','mHawala_logo.png','azizibank_logo.png','iba_logo.svg','pashtany_bank.svg'].map((img, i) => (
              <div key={i} className="bank-logo">
                <img src={`/images/${img}`} alt={img.split('_')[0]} loading="lazy"
                  onError={e => { e.target.style.display='none'; e.target.parentElement.innerText=img.split('_')[0]; }} />
              </div>
            ))}
            {/* Duplicate for seamless loop */}
            {['hesabpay_logo.png','AIB_logo.png','AUB_logo.png','mHawala_logo.png','azizibank_logo.png','iba_logo.svg','pashtany_bank.svg'].map((img, i) => (
              <div key={`dup-${i}`} className="bank-logo">
                <img src={`/images/${img}`} alt={img.split('_')[0]} loading="lazy"
                  onError={e => { e.target.style.display='none'; e.target.parentElement.innerText=img.split('_')[0]; }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── USP ── */}
      <section className="section-usp">
        <div className="container-large">
          <div className="usps-parent">
            <div className="usps-child left">
              {[
                { cls: 'first', title: 'Affordable', desc: 'Enjoy one of the lowest prices in the market.', circles: 1 },
                { cls: 'second', title: 'Speed', desc: 'Transfer and receive payments with unmatched speed.', circles: 2 },
                { cls: 'third', title: 'Secure', desc: 'Uses world class security protocols to ensure your transactions are safe.', circles: 3 },
              ].map(({ cls, title, desc, circles }) => (
                <div key={title} className={`usp-card ${cls}`} data-animate>
                  <div className="usp-icon_wrap">
                    <div className="icon-circles">
                      {Array.from({ length: circles }).map((_, i) => (
                        <span key={i} className={i === 0 ? 'circle__one' : i === 1 ? 'circle__two' : 'circle__tr'}></span>
                      ))}
                    </div>
                  </div>
                  <div className="usp-card-text">
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="usps-child right">
              <div className="usps-content">
                <h2>Unable to make <span className="highlight js-text-underline" data-underline="sm">Digital Payments?</span></h2>
                <p>Join payments evolution with us. Make online payments that are settled in seconds.</p>
                <a href="#cta" className="m-button__wrap">
                  <span className="m-b-text" aria-hidden="true">
                    <span className="m-b-text-static">Get started</span>
                    <span className="m-b-text-up">Get started</span>
                  </span>
                  <span className="m-b-arrow" aria-hidden="true">
                    <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                    <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                  </span>
                  <span className="sr-only">Get started</span>
                </a>
              </div>
              <div className="hero-visual">
                <PhoneMockup />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PAYMENT GRID ── */}
      <section className="section-payment-grid" id="payment-grid">
        <div className="container-medium">
          <div className="p-grid-title">
            <h2>One payment.<br /><span className="highlight">One Chattak.</span></h2>
            <p>Making digital payments faster by connecting all your accounts.</p>
          </div>
          <div className="payment-grid-parent">
            <div className="payment-child left">
              <div className="p-banks-wrap first">
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/AUB_logo.png" alt="AUB" loading="lazy" /></div><div className="bank-name-usps">AUB</div></div>
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/AIB_logo.png" alt="AIB" loading="lazy" /></div><div className="bank-name-usps">AIB</div></div>
              </div>
              <div className="p-banks-wrap">
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/hesabpay_logo.png" alt="HesabPay" loading="lazy" /></div><div className="bank-name-usps">HesabPay</div></div>
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/mHawala_logo.png" alt="mHawala" loading="lazy" /></div><div className="bank-name-usps">mHawala</div></div>
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/azizibank_logo.png" alt="Azizi Bank" loading="lazy" /></div><div className="bank-name-usps">Azizi Bank</div></div>
              </div>
              <div className="p-banks-wrap last">
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/iba_logo.svg" alt="IBA" loading="lazy" /></div><div className="bank-name-usps">IBA</div></div>
                <div className="bank-box"><div className="bank-logo-usps"><img src="/images/pashtany_bank.svg" alt="Pashtany Bank" loading="lazy" /></div><div className="bank-name-usps">Pashtany Bank</div></div>
              </div>
            </div>
            <div className="payment-child center">
              <div className="symbol-wrapper">
                <img src="/images/chattak_logo_white.png" alt="Chattak" />
              </div>
              <div className="svg-links">
                <svg viewBox="0 0 612 554" className="svg-conn" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2"               >
                  <path className="conn-line"
                  d="M 0 25 C 325 25 325 0 325 125 L 325 475 C 325 550 325 525 0 525 M 580 300 L 325 300 "  />
                 </svg>
              </div>
            </div>
            <div className="payment-child right">
              <div className="merchant-card" id="merchant-card">
                <div className="merchant-img"><img src="/images/qr_code.svg" alt="qr_code" loading="lazy" /></div>
                <div className="merchant-content">
                  <div className="merchant-label">Airline Ticket</div>
                  <div className="merchant-price">5000 ؋</div>
                  <button className="merchant-btn" type="button" onClick={() => setShowTransaction(true)}>Scan to Pay</button>
                </div>
              </div>
              <div className={`transaction-card${showTransaction ? ' is-active' : ''}`}>
                <div className="t-icon" aria-hidden="true">
                  <svg className="icon-success" viewBox="0 0 98 98" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="49" cy="49" r="44" className="ring" />
                    <path d="M30 51 L44 65 L70 37" className="check" />
                  </svg>
                </div>
                <div className="t-title">Successful<br />transaction</div>
                <button className="transaction-close" type="button" aria-label="Close" onClick={() => setShowTransaction(false)}>&times;</button>
                <div className="t-pill"><span className="pill-ic" aria-hidden="true">⇄</span><span>Payment is successful!</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HORIZONTAL USP ── */}
      <section className="section-horizontal-usp">
        <div className="container-large">
          <div className="horizontal-usp-parent">
            <div className="horizontal-usp-header">
              <h2>What makes <span className="highlight js-text-underline">Chattak</span> different?</h2>
              <a href="#" className="m-button__wrap" data-btn="up">
                <span className="m-b-bg-dark" aria-hidden="true"></span>
                <span className="m-b-text" aria-hidden="true">
                  <span className="m-b-text-static">Learn More</span>
                  <span className="m-b-text-up">Learn More</span>
                </span>
                <span className="m-b-arrow" aria-hidden="true">
                  <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" /></span>
                  <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" /></span>
                </span>
              </a>
            </div>
            <div className="horizontal-cards-wrap">
              <div className="horizontal-card" data-animate>
                <div className="h-card-icon"><div className="icon-shape circle-large"></div><div className="icon-shape semicircle"></div></div>
                <div className="h-card-text"><h3>State of the Art and Easy to Use UI</h3><p>You dont have to be an expert in Internet to use Chattak for day to day payments.</p></div>
              </div>
              <div className="horizontal-card" data-animate>
                <div className="h-card-icon right"><div className="icon-shape circle-small"></div><div className="icon-shape circle-small-2"></div></div>
                <div className="h-card-text"><h3>Block Chain Level Security</h3><p>Secure transactions with blockchain technology to ensure maximum security.</p></div>
              </div>
              <div className="horizontal-card" data-animate>
                <div className="h-card-icon"><div className="icon-shape square-1"></div><div className="icon-shape square-2"></div></div>
                <div className="h-card-text"><h3>Advanced KYC</h3><p>Verify users' identities and reduce fraud.</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WIZARD ── */}
      <WizardSection />

      {/* ── TABS ── */}
      <TabsSection />

      {/* ── API / BLOCKS ── */}
      <section className="section-blocks" id="api">
        <div className="container-medium">
          <div className="blocks-title">
            <h2>Designing to scale your <span className="highlight js-text-underline" data-underline="sm">business</span></h2>
          </div>

          <div className="block-wrap first">
            <div className="block-content">
              <div className="block-text">
                <h3>Building in mind for <span className="api-highlight"> businesses</span></h3>
                <p>Go from idea to implementation quickly with robust, bank-grade APIs. Our documentation, sample data and code snippets will provide companies, businesses, and merchants to integrate quickly and efficiently.</p>
              </div>
              <div className="block-image"><APIDashboard /></div>
            </div>
          </div>

          <div className="block-wrap second">
            <div className="block-content reverse">
              <div className="block-image">
                <div className="dashboard-mockup transactions">
                  <div className="dashboard-header">
                    <h4>Activity</h4>
                    <div className="filter-pills">
                      <span className="pill">All</span>
                      <span className="pill">Successful</span>
                      <span className="pill">Pending</span>
                    </div>
                  </div>
                  <div className="transactions-list">
                    {[
                      { status: 'success', icon: '/icons/airline.svg', name: 'Aria**Airline', time: '2 hours ago', amount: '؋ 8000.00' },
                      { status: 'pending', icon: '/icons/p2p.svg', name: 'Friend', time: '5 hours ago', amount: '؋ 250.00' },
                      { status: 'success', icon: '/icons/restaurant.svg', name: 'Bukh Restaurant', time: '1 day ago', amount: '؋ 1,200.00' },
                    ].map(tx => (
                      <div key={tx.name} className={`transaction-row ${tx.status}`}>
                        <div className="tx-icon"><img src={tx.icon} alt="" /></div>
                        <div className="tx-details"><div className="tx-name">{tx.name}</div><div className="tx-date">{tx.time}</div></div>
                        <div className="tx-amount">{tx.amount}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="block-text">
                <h2>All your transactions and data in <span className="api-highlight">one place</span></h2>
                <p>Manage your payments with ease. Chattak lets you send, receive, and track transactions instantly, so you can focus on life or business while we handle the rest.</p>
              </div>
            </div>
          </div>

          <div className="block-wrap third">
            <div className="block-content">
              <div className="block-text">
                <h2>Payments <span className="api-highlight">insights</span></h2>
                <h5 className="subtitle">Track. Analyze. Decide.</h5>
                <p>Visualize your transactions, monitor spending, and track incoming and outgoing payments. Chattak makes it easy to get the insights you need, whether for personal finance or business.</p>
              </div>
              <div className="block-image">
                <div className="dashboard-mockup intelligence">
                  <div className="chart-header">
                    <h4>Revenue Overview</h4>
                    <select className="chart-select"><option>Last 30 days</option><option>Last 7 days</option></select>
                  </div>
                  <div className="chart-area">
                    <svg viewBox="0 -35 405 200" className="line-chart" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" style={{ stopColor: 'var(--primary-purple)', stopOpacity: 0.2 }} />
                          <stop offset="100%" style={{ stopColor: 'var(--primary-purple)', stopOpacity: 0 }} />
                        </linearGradient>
                      </defs>
                      <path d="M0,120 Q50,110 80,130 T150,70 T220,90 T300,40 T400,60 L400,150 L0,150 Z" fill="url(#gradient)" />
                      <path d="M0,120 Q50,110 80,130 T150,70 T220,90 T300,40 T400,60" fill="none" stroke="var(--primary-purple)" strokeWidth="4" strokeLinecap="round" />
                      <circle cx="400" cy="60" r="6" fill="var(--primary-purple)" stroke="white" strokeWidth="2" />
                    </svg>
                  </div>
                  <div className="chart-stats">
                    <div className="stat"><div className="stat-label">Total Revenue</div><div className="stat-value">؋ 45,231</div></div>
                    <div className="stat"><div className="stat-label">Transactions</div><div className="stat-value">134</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section className="section-use-cases" id="use-cases">
        <div className="container-medium">
          <div className="use-cases-title">
            <h2>One wallet. <span className="highlight js-text-underline" data-underline="sm">Many Uses.</span></h2>
          </div>
          <div className="use-cases-grid">
            {[
              { icon: '/icons/p2p.svg', title: 'Individuals & P2P', desc: 'Send, receive, and store money securely. Pay friends, family, and contacts instantly using your Chattak wallet.' },
              { icon: '/icons/merchant_icon.svg', title: 'Merchant & SMEs', desc: 'Accept digital payments in-store or online. Get paid instantly, track sales, and reduce reliance on cash.' },
              { icon: '/icons/gov_icon.svg', title: 'Government & Aid', desc: 'Digitize salaries, pensions, aid distribution, and fee collection with transparency and full traceability.' },
              { icon: '/icons/utilities_icon.svg', title: 'Utilities & Bills', desc: 'Pay electricity, water, telecom, and internet bills directly from the wallet — fast, simple, and reliable.' },
              { icon: '/icons/marketplace_icon.svg', title: 'Services & Marketplaces', desc: 'Enable seamless payments for transport, delivery, ride-hailing, and local service platforms.' },
              { icon: '/icons/salary_icon.svg', title: 'Transfers & Remittances', desc: 'Move money safely across regions with instant domestic transfers and future cross-border capabilities.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="use-case-card">
                <div className="use-case-icon"><img src={icon} alt={title} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <NebulaCTA onOpenWaitlist={() => setWaitlistOpen(true)} />

      <Footer />

      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </>
  );
}
